import { Injectable } from "@angular/core";
import Dexie from 'dexie';
import { Order } from "../entities/order";
import { Region } from "../entities/region";
import { Warehouse } from "../entities/warehouse";
import { Motive } from "../entities/motive";
import { User } from "../entities/user";
import { Establishment } from "../entities/establishment";
import { Approver } from "../entities/approver";
import { Product } from "../entities/product";
import { Stock } from "../entities/stock";
import { SyncData } from "../entities/sync-data";
import { SyncLog } from "../entities/sync-log";
import { LocalConfiguration, LocalConfigurationType } from "../entities/local-configuration";

@Injectable({
  providedIn: 'root'
})
export class LocalDbService extends Dexie {
  ordersTable!: Dexie.Table<Order, string>;
  regionsTable!: Dexie.Table<Region, number>;
  userRegionsTable!: Dexie.Table<Region, number>;
  warehouseTable!: Dexie.Table<Warehouse, number>;
  motivesTable!: Dexie.Table<Motive, number>;
  productsTable!: Dexie.Table<Product, number>;
  userTable!: Dexie.Table<User, string>;
  establishmentTable!: Dexie.Table<Establishment, number>;
  approverTable!: Dexie.Table<Approver, [number, number]>;
  productTable!: Dexie.Table<Product, number>;
  stockTable!: Dexie.Table<Stock, [number, number]>;
  syncDataTable!: Dexie.Table<SyncData, string>;
  syncLogsTable!: Dexie.Table<SyncLog, number>;
  localConfigurationsTable!: Dexie.Table<LocalConfiguration, [string, LocalConfigurationType]>;

  constructor() {
    super('StockConsumableManagerDb');

    this.version(1).stores({
      ordersTable: 'id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId',
      approverTable: 'id',
    });

    this.version(2).stores({
      ordersTable: 'id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      productsTable: 'id',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId, regionId',
      approverTable: 'id',
    });

    this.version(3).stores({
      ordersTable: 'id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      productsTable: 'id',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId, regionId',
      approverTable: 'id',
      stockTable: '[warehouseId+productId], warehouseId, productId',
    });

    this.version(4).stores({
      ordersTable: 'localId, id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      productsTable: 'id',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId, regionId',
      approverTable: 'id',
      stockTable: '[warehouseId+productId], warehouseId, productId',
    });

    this.version(7).stores({
      ordersTable: 'localId, id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState, pendingSend',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      productsTable: 'id',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId, regionId',
      approverTable: 'id',
      stockTable: '[warehouseId+productId], warehouseId, productId',
      syncDataTable: 'id',
      syncLogsTable: '++id, orderId, orderState, createdAt',
      localConfigurationsTable: '[userId+type], userId, type',
    });

    this.version(8).stores({
      ordersTable: 'localId, id, requesterId, approverId, originWarehouseId, destinationWarehouseId, orderState, pendingSend',
      regionsTable: 'locationId',
      userRegionsTable: 'locationId',
      warehouseTable: 'id, regionId, warehouseManagerId',
      motivesTable: 'reasonId',
      productsTable: 'id',
      userTable: 'email, id',
      establishmentTable: 'locationId, zoneId, regionId',
      approverTable: '[id+locationId], id, locationId',
      stockTable: '[warehouseId+productId], warehouseId, productId',
      syncDataTable: 'id',
      syncLogsTable: '++id, orderId, orderState, createdAt',
      localConfigurationsTable: '[userId+type], userId, type',
    });

    this.ordersTable = this.table('ordersTable');
    this.regionsTable = this.table('regionsTable');
    this.userRegionsTable = this.table('userRegionsTable');
    this.warehouseTable = this.table('warehouseTable');
    this.motivesTable = this.table('motivesTable');
    this.productsTable = this.table('productsTable');
    this.userTable = this.table('userTable');
    this.establishmentTable = this.table('establishmentTable');
    this.approverTable = this.table('approverTable');
    this.stockTable = this.table('stockTable');
    this.syncDataTable = this.table('syncDataTable');
    this.syncLogsTable = this.table('syncLogsTable');
    this.localConfigurationsTable = this.table('localConfigurationsTable');
  }
}
